import { create } from 'zustand';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

export type UserRole = 'admin' | 'operator';

interface AuthState {
  session: Session | null;
  user: User | null;
  role: UserRole | null;
  loading: boolean;
  initialize: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

let authSubscription: { unsubscribe: () => void } | null = null;

async function fetchRole(userId: string): Promise<UserRole | null> {
  const { data, error } = await supabase
    .from('users')
    .select('role')
    .eq('id', userId)
    .maybeSingle();

  if (error || !data) return null;
  return data.role as UserRole;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  session: null,
  user: null,
  role: null,
  loading: true,

  initialize: async () => {
    if (authSubscription) return;

    const { data } = await supabase.auth.getSession();
    const initialSession = data.session;
    const role = initialSession ? await fetchRole(initialSession.user.id) : null;

    set({
      session: initialSession,
      user: initialSession?.user ?? null,
      role,
      loading: false,
    });

    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, session) => {
      const nextRole = session ? await fetchRole(session.user.id) : null;
      set({
        session,
        user: session?.user ?? null,
        role: nextRole,
      });
    });
    authSubscription = sub.subscription;
  },

  signIn: async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };

    const userId = get().user?.id ?? (await supabase.auth.getUser()).data.user?.id;
    if (userId) {
      const role = await fetchRole(userId);
      set({ role });
    }
    return { error: null };
  },

  signOut: async () => {
    await supabase.auth.signOut();
    set({ session: null, user: null, role: null });
  },
}));
